import type { SignalValues } from './conciergeState'

// The debounce *intervals* live with the watchers in the component; the *decisions*
// live here.

// A long sentence that parsed to too little still deserves a search: the buyer sees
// results and the backend can flag it refinable. Below this it's too sparse to beat
// the instant local results.
export const MIN_FALLBACK_WORDS = 8

export function wordCount (text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

export function shouldFetch (raw: string, ready: boolean): boolean {
  if (!raw.trim()) return false
  return ready || wordCount(raw) >= MIN_FALLBACK_WORDS
}

// Dedup key for the last fetch: the serialized query when ready (so a wording-only
// edit that leaves the params unchanged doesn't reflow), the raw text in the fallback
// case (so a new long sentence re-searches even though it parsed to the same sparse
// params).
export function fetchKey (ready: boolean, querySignature: string, raw: string): string {
  return ready ? querySignature : `raw:${raw.trim()}`
}

export type RefineInputs = {
  resultsRefinable: boolean
  hasUnmatched: boolean
  hasGap: boolean
  lastRefinedText: string
}

// Spend a (throttled, paid) AI refine once the buyer has added words since the last
// parse AND it's worth it — the deterministic result is weak: the backend flagged the
// results thin, OR the buyer wrote content the parser couldn't place while a signal
// slot is still empty (a misspelled/unrecognized city, occasion, relationship, or
// interest the AI can recover into that gap). With every slot already filled, leftover
// words aren't worth a call.
export function shouldRefine (
  raw: string,
  { resultsRefinable, hasUnmatched, hasGap, lastRefinedText }: RefineInputs,
): boolean {
  if (!raw.trim()) return false
  const worthwhile = resultsRefinable || (hasUnmatched && hasGap)
  if (!worthwhile) return false
  return wordCount(raw) > wordCount(lastRefinedText)
}

// The recovered signals worth merging: the AI returns null / empty for absent
// signals, and those must be dropped so an `inferred` merge never clobbers a slot
// with a blank.
export function recoveredToApply (recovered: Partial<SignalValues>): Partial<SignalValues> {
  const toApply: Partial<SignalValues> = {}
  if (recovered.relationship) toApply.relationship = recovered.relationship
  if (recovered.occasion) toApply.occasion = recovered.occasion
  if (recovered.location) toApply.location = recovered.location
  if (recovered.interests?.length) toApply.interests = recovered.interests
  return toApply
}
